import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Globe, Mail, MapPin, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_research-hub-218/artifacts/lk2wm99x_Skoop%21%20Logo%20-%202026.jpg";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', subject: '', message: ''
  });
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadCaptcha = async () => {
    try {
      const { data } = await axios.get(`${API}/captcha`);
      setCaptcha(data);
      setCaptchaAnswer('');
    } catch (err) {
      console.error('Failed to load captcha', err);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        captcha_answer: captchaAnswer,
        captcha_token: captcha?.token || ''
      });
      setSubmitStatus({ type: 'success', message: 'Your message has been sent! We\'ll get back to you soon.' });
      setFormData({ first_name: '', last_name: '', email: '', phone: '', subject: '', message: '' });
      setCaptchaAnswer('');
      loadCaptcha();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong.';
      setSubmitStatus({ type: 'error', message: typeof msg === 'string' ? msg : JSON.stringify(msg) });
      loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#0A192F] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={LOGO_URL} alt="SKOOP! Logo" className="h-24 w-auto mx-auto mb-8 animate-fade-up p-2 bg-white/10 rounded-2xl backdrop-blur-sm" style={{ filter: 'drop-shadow(0 0 30px rgba(56,189,248,0.3))' }} />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 animate-fade-up animate-delay-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Let's Connect
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto animate-fade-up animate-delay-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact info */}
            <div className="lg:col-span-1 space-y-6">
              <h2 className="text-2xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Reach Out
              </h2>
              
              <div className="neo-card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0A192F] text-[#38BDF8] flex items-center justify-center shrink-0">
                    <Globe size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Website</p>
                    <a href="https://www.skoopresearch.com" data-testid="contact-website-link" className="text-sm text-[#475569] hover:text-[#0A192F] transition-colors">
                      www.skoopresearch.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="neo-card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#166534] text-white flex items-center justify-center shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Email</p>
                    <a href="mailto:feedback@interceptinsight.com" data-testid="contact-email-link" className="text-sm text-[#475569] hover:text-[#0A192F] transition-colors">
                      feedback@interceptinsight.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="neo-card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#DC2626] text-white flex items-center justify-center shrink-0">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Location</p>
                    <p className="text-sm text-[#475569]">Avon, Colorado</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div className="lg:col-span-2">
              <div className="neo-card p-8 sm:p-10">
                <h3 className="text-xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Send Us a Message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="c_first_name" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">First Name</Label>
                      <Input
                        id="c_first_name"
                        data-testid="contact-first-name"
                        className="neo-input h-12"
                        placeholder="First name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="c_last_name" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Last Name</Label>
                      <Input
                        id="c_last_name"
                        data-testid="contact-last-name"
                        className="neo-input h-12"
                        placeholder="Last name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="c_email" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Email Address</Label>
                      <Input
                        id="c_email"
                        type="email"
                        data-testid="contact-email"
                        className="neo-input h-12"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="c_phone" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Phone Number</Label>
                      <Input
                        id="c_phone"
                        type="tel"
                        data-testid="contact-phone"
                        className="neo-input h-12"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c_subject" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Subject</Label>
                    <Input
                      id="c_subject"
                      data-testid="contact-subject"
                      className="neo-input h-12"
                      placeholder="What's this about?"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="c_message" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Message</Label>
                    <Textarea
                      id="c_message"
                      data-testid="contact-message"
                      className="neo-input min-h-[140px]"
                      placeholder="Tell us more..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                    />
                  </div>

                  {/* CAPTCHA */}
                  <div className="p-5 bg-[#F8FAFC] rounded-xl border-2 border-[#0A192F]" data-testid="captcha-section">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-semibold text-[#0A192F]">Security Check</Label>
                      <button
                        type="button"
                        onClick={loadCaptcha}
                        data-testid="captcha-refresh-btn"
                        className="p-1.5 rounded-lg hover:bg-[#0A192F]/10 transition-colors text-[#0A192F]"
                      >
                        <RefreshCw size={16} />
                      </button>
                    </div>
                    {captcha && (
                      <div className="flex items-center gap-4">
                        <span className="text-base font-bold text-[#0A192F] bg-white px-4 py-2 rounded-lg border-2 border-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {captcha.question}
                        </span>
                        <Input
                          data-testid="captcha-answer-input"
                          className="neo-input h-11 w-24"
                          placeholder="Answer"
                          value={captchaAnswer}
                          onChange={(e) => setCaptchaAnswer(e.target.value)}
                          required
                        />
                      </div>
                    )}
                  </div>

                  {submitStatus && (
                    <div
                      data-testid="contact-status"
                      className={`p-4 rounded-lg text-sm font-medium ${
                        submitStatus.type === 'success'
                          ? 'bg-[#166534]/10 text-[#166534] border-2 border-[#166534]'
                          : 'bg-[#DC2626]/10 text-[#DC2626] border-2 border-[#DC2626]'
                      }`}
                    >
                      {submitStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="neo-btn w-full py-3.5 bg-[#0A192F] text-white text-base disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
